/**
 * StatusDot — the single most-used atom in the OMEGA dashboard.
 *
 * One filled circle, 8 px, painted with the `LiveStatus` palette
 * defined in `theme/omega-theme.css`. Used on every brain tile,
 * every organ row, the top bar, and the L1 OMEGA status section.
 *
 * Zero text. Add a sibling label when one is needed. The dot's
 * meaning is encoded by colour alone — readable at a glance from
 * across a desk, which is the whole point of the command-center
 * visual register.
 */

import type { LiveStatus } from '../data/types';

const COLOR_BY_STATUS: Record<LiveStatus, string> = {
  healthy: 'var(--omega-state-healthy)',
  degraded: 'var(--omega-state-degraded)',
  critical: 'var(--omega-state-critical)',
  paused: 'var(--omega-state-paused)',
  offline: 'var(--omega-state-offline)',
  live: 'var(--omega-state-healthy)',
  blueprint: 'var(--omega-state-paused)',
};

export function StatusDot({
  status,
  size = 8,
  pulse = false,
}: {
  status: LiveStatus;
  /** Pixel diameter. Default 8 — the spec's standard tile dot. */
  size?: number;
  /** Subtle pulse on `critical`. Off by default (the spec forbids
   *  motion above 250ms — pulse is 1.6s, used only when explicitly
   *  requested). */
  pulse?: boolean;
}) {
  return (
    <span
      role="status"
      aria-label={`status: ${status}`}
      style={{
        display: 'inline-block',
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: '50%',
        background: COLOR_BY_STATUS[status],
        flexShrink: 0,
        animation: pulse ? 'omega-pulse 1.6s ease-in-out infinite' : undefined,
      }}
    />
  );
}
